import {
  Alert,
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  InputNumber,
  Row,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd'
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { todayInTenantZone } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { COMMISSION_KINDS } from './agentApi'
import { PERCENT_MAX, TIER_MAX, TIER_VALUE_KINDS } from './commissionTiers'
import type { CommissionState } from './commissionForm'

export interface CommissionFieldsProps {
  currency: string
  state: CommissionState
  /** `null` — limit qo'yilmagan (cheksiz); o'lik pog'ona ogohlantirishi uchun */
  perTxnLimitMinor: number | null
  /** Ichma-ich forma uchun nom prefiksi. Bo'sh — muharrir oynasidagidek tekis. */
  prefix?: readonly string[]
  /** Tur tanlanmasligi mumkinmi (yaratish formasida komissiya IXTIYORIY) */
  allowNone?: boolean
  /**
   * «Qachondan kuchga kiradi» maydoni.
   *
   * ⚠️ Yaratish formasida CHIZILMAYDI: yangi agentda o'tmish yo'q,
   * ya'ni kelajak sanasi tanlash faqat «komissiyasiz agent» oralig'ini
   * yaratardi va uni hech kim kutmaydi. Qoida darhol kuchga kiradi.
   */
  showValidFrom?: boolean
}

/**
 * Komissiya qoidasining MAYDONLARI — foiz / qat'iy summa / zinapoya.
 *
 * <h2>⚠️ NEGA ALOHIDA FAYL</h2>
 * Bu blok IKKI joyda kerak: mavjud «Qoida o'rnatish» oynasida va
 * agent/reseller YARATISH formasida (egasi, 2026-08-20: «Agent
 * yaratilganda foizini ham shu yerda sozlab ketish kerak»). Mantiqni
 * nusxalash ikki muharrirni bir kunda ajratib yuborardi: zinapoya
 * tekshiruvi bittasida yangilanib, ikkinchisida eskirardi.
 *
 * <h2>⚠️ PAKET TANLAGICHI YO'Q (egasi qarori, 2026-08-19)</h2>
 * Komissiya faqat BALANS TO'LDIRISHDA hisoblanadi, to'ldirishda esa
 * paket tushunchasi umuman YO'Q. Server `planId` bilan kelgan qoidani
 * 400 bilan rad etadi (SHARTNOMA §1).
 *
 * <h2>⚠️ FOIZ MAYDONI `InputNumber` (XSB-COM-001)</h2>
 * Ilgari bu yerda `<Input type="number">` turardi va u SATR qaytarardi
 * — `typeof … === 'number'` tekshiruvlari yiqilib, `PERCENT` qoidasi
 * `percentBp: null` bilan ketardi, `TIER` da esa «Saqlash» tugmasi
 * mangu o'chiq qolardi. `InputNumber` haqiqiy son beradi; ustiga
 * `toPercentNumber` chegarada ikkinchi qavat sifatida turadi.
 */
export function CommissionFields({
  currency,
  state,
  perTxnLimitMinor,
  prefix = [],
  allowNone = false,
  showValidFrom = true,
}: CommissionFieldsProps) {
  const { t } = useTranslation()
  const { kind, tierRows, problems, dead } = state

  return (
    <>
      <Form.Item name={[...prefix, 'kind']} label={t('agents.commissionKind')}>
        <Select
          allowClear={allowNone}
          placeholder={allowNone ? t('agents.commissionNonePlaceholder') : undefined}
          options={COMMISSION_KINDS.map((k) => ({ value: k, label: t(`commissionKind.${k}`) }))}
        />
      </Form.Item>

      {kind === 'PERCENT' ? (
        <Form.Item
          name={[...prefix, 'percent']}
          label={t('agents.commissionPercent')}
          extra={t('agents.commissionPercentHint')}
          rules={[{ required: true, message: t('agents.commissionValueRequired') }]}
        >
          <InputNumber
            min={0}
            max={PERCENT_MAX}
            step={0.01}
            addonAfter="%"
            style={{ width: '100%' }}
          />
        </Form.Item>
      ) : null}

      {kind === 'FIXED' ? (
        <Form.Item
          name={[...prefix, 'fixedAmount']}
          label={t('agents.commissionFixed')}
          rules={[{ required: true, message: t('agents.commissionValueRequired') }]}
        >
          <MoneyInput currency={currency} minMinor={0} />
        </Form.Item>
      ) : null}

      {kind === 'TIER' ? (
        <>
          {/*
            ⚠️ CHEGARA MA'NOSI BIR QATORDA VA ANIQ. «10 000 gacha 2,
            10 000 dan 50 000 gacha 5» degan misolda aynan 10 000
            qaysi pog'onaga tushishi TUSHUNARSIZ — va farq pulda.
            SHARTNOMA §2: `upToAmount` KIRITILMAYDI, ya'ni 10 000
            ikkinchi pog'onada.
          */}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('agents.commissionTier.boundaryTitle')}
            description={t('agents.commissionTier.boundaryHint')}
          />

          <Form.List name={[...prefix, 'tiers']}>
            {(fields, { add, remove, move }) => (
              <>
                {fields.map((field, index) => {
                  const last = index === fields.length - 1
                  const row = tierRows[index] ?? {}
                  return (
                    <Row key={field.key} gutter={8} align="top" style={{ marginBottom: 4 }}>
                      <Col flex="28px" style={{ paddingTop: 5 }}>
                        <Typography.Text type="secondary">{index + 1}</Typography.Text>
                      </Col>
                      <Col flex="1 1 190px">
                        <Form.Item
                          name={[field.name, 'upTo']}
                          label={index === 0 ? t('agents.commissionTier.upTo') : undefined}
                          style={{ marginBottom: 4 }}
                        >
                          <MoneyInput
                            currency={currency}
                            minMinor={0}
                            placeholder={t('agents.commissionTier.upToPlaceholder')}
                          />
                        </Form.Item>
                        {/*
                          ⚠️ BO'SH MAYDON IZOHSIZ QOLMAYDI. Oxirgi
                          pog'onada bo'sh «gacha» — bu xato emas,
                          «va undan yuqorisi» degani; buni yozmasak
                          operator maydonni to'ldirilmagan deb
                          o'ylardi.
                        */}
                        {last && !row.upTo ? (
                          <Tag color="blue">{t('agents.commissionTier.andAbove')}</Tag>
                        ) : null}
                        {dead.includes(index + 1) ? (
                          <Typography.Text type="warning" style={{ fontSize: 12 }}>
                            {t('agents.commissionTier.deadRow')}
                          </Typography.Text>
                        ) : null}
                      </Col>
                      <Col flex="0 0 130px">
                        <Form.Item
                          name={[field.name, 'valueKind']}
                          label={index === 0 ? t('agents.commissionKind') : undefined}
                          style={{ marginBottom: 4 }}
                        >
                          <Select
                            placeholder={t('agents.commissionTier.valueKindPlaceholder')}
                            options={TIER_VALUE_KINDS.map((k) => ({
                              value: k,
                              label: t(`commissionKind.${k}`),
                            }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col flex="1 1 160px">
                        {row.valueKind === 'FIXED' ? (
                          <Form.Item
                            name={[field.name, 'fixed']}
                            label={index === 0 ? t('agents.commissionValue') : undefined}
                            style={{ marginBottom: 4 }}
                          >
                            <MoneyInput currency={currency} minMinor={0} />
                          </Form.Item>
                        ) : (
                          <Form.Item
                            name={[field.name, 'percent']}
                            label={index === 0 ? t('agents.commissionValue') : undefined}
                            style={{ marginBottom: 4 }}
                          >
                            <InputNumber
                              min={0}
                              max={PERCENT_MAX}
                              step={0.01}
                              addonAfter="%"
                              style={{ width: '100%' }}
                              disabled={row.valueKind !== 'PERCENT'}
                            />
                          </Form.Item>
                        )}
                      </Col>
                      <Col flex="0 0 108px" style={{ paddingTop: index === 0 ? 30 : 0 }}>
                        <Space size={0}>
                          <Button
                            type="text"
                            size="small"
                            icon={<ArrowUpOutlined />}
                            disabled={index === 0}
                            aria-label={t('agents.commissionTier.moveUp')}
                            title={t('agents.commissionTier.moveUp')}
                            onClick={() => move(index, index - 1)}
                          />
                          <Button
                            type="text"
                            size="small"
                            icon={<ArrowDownOutlined />}
                            disabled={last}
                            aria-label={t('agents.commissionTier.moveDown')}
                            title={t('agents.commissionTier.moveDown')}
                            onClick={() => move(index, index + 1)}
                          />
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            aria-label={t('agents.commissionTier.removeRow')}
                            title={t('agents.commissionTier.removeRow')}
                            onClick={() => remove(field.name)}
                          />
                        </Space>
                      </Col>
                    </Row>
                  )
                })}

                {/* ⚠️ Chegara ekranda OLDINDAN yozilmaydi — u biznes
                    qoidasi emas, cheksiz o'sishdan himoya. Faqat
                    unga YETILGANDA aytiladi. */}
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => add({})}
                  disabled={fields.length >= TIER_MAX}
                  style={{ marginTop: 8 }}
                >
                  {t('agents.commissionTier.addRow')}
                </Button>
                {fields.length >= TIER_MAX ? (
                  <Typography.Text type="secondary" style={{ marginInlineStart: 8 }}>
                    {t('agents.commissionTier.maxReached', { max: TIER_MAX })}
                  </Typography.Text>
                ) : null}
              </>
            )}
          </Form.List>

          {/* ⚠️ Zinapoya `PER_TXN` shifti OSTIDA ishlaydi (egasi,
              2026-08-19): bitta to'ldirish shu limitdan oshmaydi,
              ya'ni oxirgi pog'ona amalda shu limitgacha bo'lgan
              summani qamraydi. */}
          <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
            {perTxnLimitMinor === null
              ? t('agents.commissionTier.perTxnUnlimited')
              : t('agents.commissionTier.perTxnCap', {
                  limit: formatMoneyValue({ amount: perTxnLimitMinor, currency }),
                })}
          </Typography.Paragraph>

          {problems.length > 0 ? (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 12 }}
              message={t('agents.commissionTier.invalid')}
              description={
                <ul style={{ margin: 0, paddingInlineStart: 20 }}>
                  {problems.map((problem) => (
                    <li key={`${problem.code}:${String(problem.row)}`}>
                      {problem.row === null
                        ? t(`agents.commissionTier.error.${problem.code}`, { max: TIER_MAX })
                        : t(`agents.commissionTier.error.${problem.code}`, {
                            row: problem.row,
                            max: TIER_MAX,
                          })}
                    </li>
                  ))}
                </ul>
              }
            />
          ) : null}
        </>
      ) : null}

      {showValidFrom && kind ? (
        <>
          <Divider style={{ margin: '16px 0' }} />
          {/*
            ⚠️ «QACHONDAN KUCHGA KIRADI» (SHARTNOMA §5). Har tranzaksiya
            O'SHA ONDA kuchda bo'lgan qoida bilan hisoblanadi, o'tmish
            esa QAYTA HISOBLANMAYDI. Buni yozmasak operator kechagi
            to'lovlar ham yangi foiz bilan qayta hisoblanadi deb
            o'ylardi — va oy oxirida raqamlar mos kelmasdi.
          */}
          <Form.Item
            name={[...prefix, 'validFrom']}
            label={t('agents.commissionValidFrom')}
            extra={t('agents.commissionValidFromHint')}
          >
            {/*
              ⚠️ «BUGUN» TENANT ZONASIDA hisoblanadi, brauzernikida emas
              (ADR-015, `tests/timezone.mjs`). `dayjs().startOf('day')`
              yozilsa Kobuldagi operator uchun kun chegarasi bir necha
              soatga siljirdi va u bugungi ondan boshlanadigan qoidani
              «o'tmish» deb rad etilganini ko'rardi.
            */}
            <DatePicker
              showTime
              style={{ width: '100%' }}
              placeholder={t('agents.commissionValidFromNow')}
              disabledDate={(value) => value.format('YYYY-MM-DD') < todayInTenantZone()}
            />
          </Form.Item>
          <Typography.Text type="warning">{t('agents.commissionNoRecalc')}</Typography.Text>
        </>
      ) : null}
    </>
  )
}
